<template>
  <div class="SearchPage">
    <header class="page__header">
      <h1 class="page__title">
        Search results for '{{ query }}'
      </h1>
    </header>

    <aside>
      <label for="sort_by">Sort by</label>
      <div class="select">
        <select v-model="sortBy" @change="searchResult">
          <option value="relevance" selected>Relevance</option>
          <option value="newest">Published Date (newest first)</option>
          <option value="oldest">Published Date (oldest first)</option>
          <option value="interested">Most Interested</option>
        </select>
      </div>
    </aside>

    <div class="SearchPage__results container">
      <div class="SearchPage__results-articles">
        <h2>Articles</h2>
        <div v-if="search.articles.length > 0" class="article-blocks">
          <ArticleBlockComponent v-for="article in search.articles" :key="article.id" :article="article" />

          <a v-if="hasMoreArticles" class="button button--block button--primary" role="button" @click="loadMoreArticles">
            <div v-if="isLoadingArticle" class="spinner-border" role="status">
              <span class="sr-only" />
            </div>
            <div v-else>
              View more Articles
            </div>
          </a>
        </div>
        <p v-else>
          No articles found
        </p>
      </div>


      <div class="SearchPage__results-others">
        <h2>Videos</h2>
        <div v-if="search.articleVideos.length > 0" class="article-blocks videos">
          <ArticleVideoBlockSearchComponent v-for="video in search.articleVideos" :key="video.id" :video="video" />

          <a v-if="hasMoreArticleVideos" class="button button--block button--primary" role="button"
            @click="loadMoreArticleVideos">
            <div v-if="isLoadingVideo" class="spinner-border" role="status">
              <span class="sr-only" />
            </div>
            <div v-else>
              View more Videos
            </div>
          </a>
        </div>
        <p v-else>
          No videos found
        </p>

        <h2>Artists</h2>
        <template v-if="search.artists.length > 0">
          <ArtistComponent v-for="artist in search.artists" :key="artist.id" :artist="artist" />
        </template>
        <p v-else>
          No artists found.
        </p>

        <h2>Gigs</h2>

        <template v-if="search.gigs.length > 0">
          <GigComponent v-for="gig in search.gigs" :key="gig.id" :gig="gig" />
        </template>
        <p v-else>
          No gigs found.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';

import ArticleBlockComponent from '../article/ArticleBlockComponent.vue';
import ArticleVideoBlockSearchComponent from '../article_video/ArticleVideoBlockSearchComponent.vue';
import ArtistComponent from '../artist/ArtistComponent.vue';
import GigComponent from '../gig/GigComponent.vue';

const SEARCH_QUERY = gql`query searchQuery($query: String!, $country: String!, $offset: Int!, $sortby:String!) {
  search(query: $query, country: $country, offset: $offset, sortby: $sortby ) {
    articles {
      id
      title
      author
      image
      homePageImage
      publishedAt
      url
      spins
      genre
      isPaid
      categories {
        name slug
      }
    }
    articleVideos {
      id
      title
      coverImage
      publishedAt
      formattedViews
      url
    }
    artists {
      id
      imageUrl
      name
      url
    }
    gigs {
      id
      title
      startsAt
      imageUrl
      url
      pick
    }
  }
}`;

export default {
  apollo: {
    search: {
      query: SEARCH_QUERY,
      variables() {
        return {
          country: this.country,
          offset: 0,
          query: this.query,
          sortby: this.sortBy,
        };
      },
    },
  },
  components: {
    ArticleBlockComponent,
    ArticleVideoBlockSearchComponent,
    ArtistComponent,
    GigComponent,
  },
  props: {
    country: {
      type: String,
      default: 'SG',
    },
    query: {
      type: String,
      default: '',
    },
    sortby: {
      type: String,
      default: 'relevance',
    },
  },
  data() {
    return {
      hasMoreArticles: true,
      hasMoreArticleVideos: true,
      isLoadingArticle: false,
      isLoadingVideo: false,
      offset: 0,
      sortBy: 'relevance',
      search: {
        articles: [],
        articleVideos: [],
        artists: [],
        gigs: [],
      },
    };
  },
  methods: {
    loadMoreArticles() {
      // SearchService loads 6 articles by default
      this.offset += 6;
      this.isLoadingArticle = true;

      // Load more articles
      this.$apollo.queries.search.fetchMore({
        // Update query with new variables
        variables: {
          country: this.country,
          offset: this.offset,
          query: this.query,
          sortby: this.sortBy,
        },
        // Add new data to existing data
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newArticles = fetchMoreResult.search.articles;

          if (newArticles.length === 0) {
            this.hasMoreArticles = false;
          }

          this.isLoadingArticle = false;

          return {
            search: {
              __typename: previousResult.search.__typename,
              // Merging the new data
              articles: [...previousResult.search.articles, ...newArticles],
              articleVideos: [...previousResult.search.articleVideos],
              artists: [...previousResult.search.artists],
              gigs: [...previousResult.search.gigs],
            },
          };
        },
      });
    },
    loadMoreArticleVideos() {
      // SearchService loads 6 articles by default
      this.offset += 6;
      this.isLoadingVideo = true;

      // Load more articles
      this.$apollo.queries.search.fetchMore({
        // Update query with new variables
        variables: {
          country: this.country,
          offset: this.offset,
          query: this.query,
          sortby: this.sortBy,
        },
        // Add new data to existing data
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newArticleVideos = fetchMoreResult.search.articleVideos;

          if (newArticleVideos.length === 0) {
            this.hasMoreArticleVideos = false;
          }

          this.isLoadingVideo = false;

          return {
            search: {
              __typename: previousResult.search.__typename,
              // Merging the new data
              articles: [...previousResult.search.articles],
              articleVideos: [...previousResult.search.articleVideos, ...newArticleVideos],
              artists: [...previousResult.search.artists],
              gigs: [...previousResult.search.gigs],
            },
          };
        },
      });
    },
    searchResult() {
      try {
        this.hasMoreArticles = true;
        this.hasMoreArticleVideos = true;
        this.isLoadingArticle = false;
        this.isLoadingVideo = false;
        this.$apollo.queries.search.fetchMore(
        {
          variables: {
            country: this.country,
            offset: this.offset,
            query: this.query,
            sortby: this.sortBy,
          },
        });
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>
